import React, { useEffect, useState } from 'react';
import { CircularProgress } from '@material-ui/core';
import { OpenInNew } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import Checkbox from 'Components/Atoms/Checkbox';
import InputAdornmentAtom from 'Components/Atoms/InputAdornment';
import TextField from 'Components/Atoms/TextField';
import Typography from 'Components/Atoms/Typography';
import Modal from 'Components/Molecules/Modal';
import SignatureDropdown from 'Components/Organisms/SignatureDropdown';
import { Severity } from 'Contexts/RestaurantContext';
import useDocument from 'CustomHooks/useDocument';
import useLanguage from 'CustomHooks/useLanguage';
import useRestaurant from 'CustomHooks/useRestaurant';
import useToast from 'CustomHooks/useToast';
import { Reservation } from 'types/reservations';
import {
  SettingsName,
  TicketCancellationSettings,
  TicketCancellationTemplate,
} from 'types/settings';
import {
  TicketRefundCalculation,
  TicketRefundCalculationType,
  TicketRefundMethod,
} from 'types/shared/documents/restaurants/event';
import { classHelper, toCurrencyString } from 'utils/helper';
import rawServer from 'utils/server';
import type { AxiosInstance } from 'axios';
const server: AxiosInstance = rawServer;
import {
  allocationWithoutServiceFee,
  CALCULATION_OPTIONS,
  calculateRefundAmount,
  describeCancellationTemplate,
  initialCancellationTemplate,
  parseDecimalInput,
  refundCalculationNeedsValue,
  refundUnavailableHint,
  suggestCancellationTemplate,
  TicketCancellationPayment,
  TicketCancellationPreview,
} from 'utils/ticketCancellation';
import './styles.scss';

export type TicketCancellationModalProps = {
  restaurantId: string;
  reservation: Reservation;
  onClose: () => void;
};

// The user either picks a template or has to choose this on purpose.
const WITHOUT_TEMPLATE = 'without-template';

const euros = (amount: number) => toCurrencyString(amount, '€', false, 1);

const localeOf = (language: string) =>
  language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : 'en-GB';

const formatDateTime = (value: number | string | null, language: string) =>
  value
    ? new Date(value).toLocaleString(localeOf(language), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '–';

const formatEventDate = (date: string, time: string, language: string) => {
  const day = new Date(`${date}T12:00:00`).toLocaleDateString(localeOf(language), {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return time ? `${day}, ${time}` : day;
};

const daysUntilEventLabel = (
  days: number | null,
  t: (key: string, options?: Record<string, unknown>) => string
) => {
  if (days === null) return '';
  if (days === 0) return t('today');
  if (days === 1) return t('tomorrow');
  if (days === -1) return t('yesterday');

  return days > 0
    ? t('in {{days}} days', { days })
    : t('{{days}} days ago', { days: -days });
};

const paymentMethodLabel = (
  payment: TicketCancellationPayment,
  t: (key: string) => string
) => {
  if (payment.paidVia === 'mollie')
    return payment.method ? `Mollie · ${t(payment.method)}` : 'Mollie';

  return t(payment.paidVia);
};

const errorMessage = (error: any, fallback: string) => {
  const message = error?.response?.data?.message ?? error?.message;

  return typeof message === 'string' && message ? message : fallback;
};

const TicketCancellationModal: React.FC<TicketCancellationModalProps> = ({
  restaurantId,
  reservation,
  onClose,
}) => {
  const { t } = useTranslation('tickets');
  const { language } = useLanguage();
  const { reservationSettings } = useRestaurant();
  const toast = useToast();
  const [settingsDocument] = useDocument<TicketCancellationSettings>(
    `/restaurants/${restaurantId}/settings`,
    SettingsName.TICKET_CANCELLATION
  );

  const [preview, setPreview] = useState<TicketCancellationPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [choice, setChoice] = useState<string | null>(null);
  const [initialChoiceMade, setInitialChoiceMade] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [calculationType, setCalculationType] =
    useState<TicketRefundCalculationType>('full');
  const [valueInput, setValueInput] = useState('');
  const [includeServiceFee, setIncludeServiceFee] = useState(true);
  const [refundMethod, setRefundMethod] = useState<TicketRefundMethod>('mollie');
  const [reason, setReason] = useState('');
  const guestEmail = reservation.guest?.email ?? '';
  const [notifyGuest, setNotifyGuest] = useState(!!guestEmail);
  const [signature, setSignature] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allocationInputs, setAllocationInputs] = useState<Record<string, string>>({});

  const templates = settingsDocument.data?.templates ?? [];
  const template = templates.find(({ id }) => id === choice) ?? null;
  const mollieUsable =
    !!preview?.mollieAvailable && (preview?.totals.mollieRefundable ?? 0) > 0;
  const refundHint = refundUnavailableHint(preview?.payments ?? []);
  const signatureRequired = !!reservationSettings?.signatureRequired;
  const suggestedTemplate = preview
    ? suggestCancellationTemplate(templates, preview.daysUntilEvent)
    : null;

  useEffect(() => {
    let unmounted = false;

    server
      .get<TicketCancellationPreview>(
        `/v03/reservations/${restaurantId}/res/${reservation.id}/ticketCancellation`
      )
      .then(({ data }) => {
        if (!unmounted) setPreview(data);
      })
      .catch((loadingError) => {
        if (!unmounted)
          setLoadError(errorMessage(loadingError, t('The payments could not be loaded.')));
      });

    return () => {
      unmounted = true;
    };
  }, [restaurantId, reservation.id, t]);

  const selectTemplate = (selected: TicketCancellationTemplate) => {
    setChoice(selected.id);
    setOptionsOpen(false);
    setCalculationType(selected.calculation.type);
    setValueInput(
      typeof selected.calculation.value === 'number'
        ? String(selected.calculation.value).replace('.', ',')
        : ''
    );
    setIncludeServiceFee(!!selected.calculation.includeServiceFee);
    setRefundMethod(selected.refundMethod);
    // Keep a reason the user typed; replace one that only came from a template.
    const previousTemplateReason = template?.reason ?? '';
    setReason((current) =>
      !current.trim() || current === previousTemplateReason ? selected.reason : current
    );
    setError(null);
  };

  const continueWithoutTemplate = () => {
    if (!template)
      setRefundMethod(mollieUsable ? 'mollie' : (preview?.totals.paid ?? 0) > 0 ? 'voucher' : 'none');

    setChoice(WITHOUT_TEMPLATE);
    setOptionsOpen(true);
    setError(null);
  };

  // Once payments and templates are loaded, start with the fitting template.
  useEffect(() => {
    if (!preview || settingsDocument.loading || initialChoiceMade) return;

    setInitialChoiceMade(true);

    const initialTemplate = initialCancellationTemplate(templates, preview.daysUntilEvent);

    if (initialTemplate) selectTemplate(initialTemplate);
  }, [preview, settingsDocument.loading, initialChoiceMade]);

  const needsValue = refundCalculationNeedsValue(calculationType);
  const enteredValue = needsValue ? parseDecimalInput(valueInput) : null;
  const calculation: TicketRefundCalculation = {
    type: calculationType,
    value: enteredValue,
    includeServiceFee: calculationType !== 'fixed' && includeServiceFee,
  };

  const refundAmount =
    preview && (!needsValue || enteredValue !== null)
      ? calculateRefundAmount(preview.totals, calculation)
      : 0;
  const effectiveMethod: TicketRefundMethod =
    preview?.operation?.refundMethod ?? refundMethod;
  const maximumAmount =
    effectiveMethod === 'mollie'
      ? preview?.totals.mollieRefundable ?? 0
      : effectiveMethod === 'originalVoucher' ? preview?.totals.originalVoucher ?? 0 : preview?.totals.paid ?? 0;
  const alreadyRefunded =
    preview?.payments.reduce((sum, payment) => sum + payment.refunded, 0) ?? 0;
  const alreadyCancelled = !!preview?.cancellation;
  const templateNeedsMollie =
    !!template && template.refundMethod === 'mollie' && !mollieUsable;

  const validationError = (() => {
    if (!preview || effectiveMethod === 'none') return null;
    if (refundAmount <= 0) return t('Please enter a positive refund amount or choose no refund.');
    if (needsValue && enteredValue === null)
      return t('Please enter a value for the refund.');
    if (calculationType === 'percentage' && (enteredValue ?? 0) > 100)
      return t('The percentage can be 100 at most.');
    if (effectiveMethod === 'mollie' && !mollieUsable)
      return t('This ticket cannot be refunded via Mollie. Choose another refund method.');
    if (effectiveMethod !== 'none' && refundAmount > maximumAmount + 0.001)
      return effectiveMethod === 'mollie'
        ? t('Mollie can refund {{amount}} at most.', { amount: euros(maximumAmount) })
        : t('You can refund {{amount}} at most.', { amount: euros(maximumAmount) });
    return null;
  })();

  const allocations: Record<string, { index: number; amountCents: number }[]> = {};
  let allocationError: string | null = null;
  let allocationInputInvalid = false;
  let unassigned = Math.round(refundAmount * 100);
  // Only the "full without service fee" choice fixes the split, never an amount that merely equals it.
  const splitWithoutFee = !preview?.operation && calculation.type === 'full' && !calculation.includeServiceFee;
  const allocationTargets = preview?.operation ? preview.operation.accountingTargets : (preview?.payments ?? []).filter(p => p.status === 'paid' && p.verified !== false &&
    (effectiveMethod === 'voucher' || (effectiveMethod === 'mollie' && p.paidVia === 'mollie')))
    .slice().sort((a, b) => effectiveMethod === 'mollie' ? b.refundable - a.refundable : b.amount - a.amount)
    .flatMap(payment => {
      const available = Math.round((payment.paidVia === 'mollie' && (effectiveMethod === 'mollie' || !payment.refundsUnavailable) ? payment.refundable : payment.amount - payment.refunded) * 100);
      const amountCents = Math.min(available, unassigned); unassigned -= amountCents;
      return payment.paidVia === 'mollie' && amountCents > 0 ? [{ paymentId: payment.id, amountCents }] : [];
    });
  for (const target of allocationTargets) {
    const context = preview?.accounting?.find(a => a.paymentId === target.paymentId);
    if (!context || context.allocationError) {
      if (effectiveMethod === 'voucher') allocationError = context?.allocationError || t('The original invoice could not be loaded.');
      continue;
    }
    const full = context.lines.reduce((sum, l) => sum + l.remainingCents, 0) === target.amountCents;
    if (full) continue;
    const withoutFee = splitWithoutFee && allocationWithoutServiceFee(context.lines, target.amountCents);
    if (withoutFee) { allocations[target.paymentId] = withoutFee; continue; }
    // On resume, blank fields keep the split stored when the cancellation started.
    if (preview?.operation && context.lines.every(line => !(allocationInputs[`${target.paymentId}:${line.index}`] ?? '').trim())) continue;
    const selected = context.lines.map(line => ({ index: line.index,
      amountCents: Math.round((parseDecimalInput(allocationInputs[`${target.paymentId}:${line.index}`] ?? '') ?? 0) * 100) }));
    if (selected.some((line, i) => line.amountCents > context.lines[i].remainingCents) ||
      selected.reduce((sum, l) => sum + l.amountCents, 0) !== target.amountCents) {
      allocationError = t('Assign the partial refund to the invoice items.');
      allocationInputInvalid = true;
    } else allocations[target.paymentId] = selected.filter(l => l.amountCents > 0);
  }

  // A template keeps its options folded away unless something needs a decision.
  const showOptions =
    choice === WITHOUT_TEMPLATE || optionsOpen || templateNeedsMollie || !!validationError;

  // The update triggers only send the cancellation email when the flag turns true.
  const cancellationEmailGoesOut =
    notifyGuest && !!guestEmail && !reservation.sendCancelationEmail;

  const refundDelivery = (() => {
    if (effectiveMethod === 'mollie')
      return t('The guest sees the refund after a few business days, depending on the payment method.');
    if (effectiveMethod !== 'voucher') return null;
    if (!guestEmail)
      return t('There is no email address: hand the voucher code to the guest after cancelling.');

    return cancellationEmailGoesOut
      ? t('The voucher (code and PDF) goes to {{email}} with the cancellation email.', {
          email: guestEmail,
        })
      : t('The voucher goes to {{email}} in its own email.', { email: guestEmail });
  })();

  const submitLabel =
    effectiveMethod === 'mollie'
      ? t('Cancel and refund {{amount}}', { amount: euros(refundAmount) })
      : effectiveMethod === 'voucher'
      ? t('Cancel and issue a voucher of {{amount}}', { amount: euros(refundAmount) })
      : effectiveMethod === 'originalVoucher' ? t('Cancel and restore {{amount}} to the original voucher', { amount: euros(refundAmount) }) : t('Cancel without refund');

  const close = () => {
    if (!saving) onClose();
  };

  const resume = async (retryFailed = false) => {
    if (!preview || saving) return;
    if (allocationInputInvalid) return setError(allocationError);
    setSaving(true); setError(null);
    try {
      await server.post(`/v03/reservations/${restaurantId}/res/${reservation.id}/ticketCancellation`, { resume: true, retryFailed, allocations });
      const { data } = await server.get<TicketCancellationPreview>(`/v03/reservations/${restaurantId}/res/${reservation.id}/ticketCancellation`);
      setPreview(data);
      setAllocationInputs({});
      if (data.operation?.error) setError(data.operation.error);
    } catch (err) { setError(errorMessage(err, t('The cancellation could not be checked.'))); }
    finally { setSaving(false); }
  };

  const submit = async () => {
    if (alreadyCancelled) return resume();
    if (!preview || !choice) return;
    if (allocationError) return setError(allocationError);

    if (validationError) return setError(validationError);

    if (!reason.trim()) return setError(t('Please enter a reason for the cancellation.'));

    if (signatureRequired && !signature) return setError(t('Signature is required'));

    setSaving(true);
    setError(null);

    try {
      const { data } = await server.post<{
        ticketRefund: {
          method: TicketRefundMethod;
          amount: number;
          voucherCode?: string | null;
          voucherEmailedTo?: string | null;
          emailStatus?: string;
          status?: string;
        };
      }>(
        `/v03/reservations/${restaurantId}/res/${reservation.id}/ticketCancellation`,
        {
          reason: reason.trim(),
          refundMethod: effectiveMethod,
          calculation,
          expectedAmount: effectiveMethod === 'none' ? 0 : refundAmount,
          notifyGuest: notifyGuest && !!guestEmail,
          templateId: template?.id ?? null,
          templateName: template?.name ?? null,
          signature,
          allocations,
        }
      );

      const { voucherCode, voucherEmailedTo, emailStatus, status } = data.ticketRefund ?? {};

      if (voucherCode && !guestEmail) {
        toast(
          t('Ticket cancelled. Hand voucher {{code}} to the guest, there is no email address.', {
            code: voucherCode,
          }),
          Severity.WARNING,
          null
        );
      } else if (voucherCode && emailStatus === 'sent') {
        toast(
          t('Ticket cancelled. Voucher {{code}} is emailed to {{email}}.', {
            code: voucherCode,
            email: voucherEmailedTo,
          }),
          Severity.SUCCESS,
          null
        );
      } else if (voucherCode) {
        toast(t('Voucher {{code}} created. Email delivery is still pending.', { code: voucherCode }), Severity.WARNING, null);
      } else if (status && status !== 'refunded' && effectiveMethod === 'mollie') {
        toast(t('Cancellation saved. The refund is not yet confirmed.'), Severity.WARNING, null);
      } else {
        toast(t('The ticket has been cancelled'), Severity.SUCCESS, null);
      }

      onClose();
    } catch (submitError) {
      const refreshed = await server.get<TicketCancellationPreview>(`/v03/reservations/${restaurantId}/res/${reservation.id}/ticketCancellation`).catch(() => null);
      if (refreshed) setPreview(refreshed.data);
      setError(errorMessage(submitError, t('The ticket could not be cancelled.')));
      setSaving(false);
    }
  };

  const templateAmountLabel = (option: TicketCancellationTemplate) => {
    if (!preview) return '';

    const amount = calculateRefundAmount(preview.totals, option.calculation);

    return option.refundMethod === 'mollie' && !mollieUsable
      ? `${euros(amount)} · ${t('Mollie is not possible here')}`
      : euros(amount);
  };

  return (
    <Modal
      open
      onClose={close}
      title="Cancel ticket"
      titleTranslation="tickets"
      maxWidth="sm"
      fullWidth
      stopAwayClick
      dontCloseAfterSubmit
      className="ticket-cancellation-modal"
      actions={[
        {
          id: 'cancel',
          label: 'Cancel',
          translation: 'common',
          onClick: close,
          disabled: saving,
        },
        {
          id: 'submit',
          label: alreadyCancelled ? t('Check cancellation again') : choice ? submitLabel : t('Choose a template first'),
          translation: null,
          variant: 'primary',
          onClick: submit,
          loading: saving,
          disabled: saving || !preview || !!preview.operation?.busy || (alreadyCancelled ? allocationInputInvalid : (!choice || !!validationError || !!allocationError)),
        },
      ]}
    >
      <Box className="ticket-cancellation-modal__content">
        {!preview && !loadError && (
          <Box className="ticket-cancellation-modal__loading">
            <CircularProgress size={24} />
          </Box>
        )}

        {!!loadError && (
          <Typography
            variant="text-3"
            translation={null}
            className="ticket-cancellation-modal__error"
            block
          >
            {loadError}
          </Typography>
        )}

        {!!preview && (
          <>
            {alreadyCancelled && <Box>
              <Typography translation={null} block>{preview.operation?.error || t('This cancellation has already started. Check the existing operation.')}</Typography>
              {preview.operation?.refundFailed && <Button translation={null} disabled={saving || preview.operation.busy} onClick={() => resume(true)}>{t('Retry the failed Mollie refund')}</Button>}
              <Button translation={null} href={`/${restaurantId}/bookkeeping/customer-invoices`}>{t('Open payment receipts')}</Button>
              {!!error && <Typography translation={null} block>{error}</Typography>}
            </Box>}
            <Box className="ticket-cancellation-modal__facts">
              <Box className="ticket-cancellation-modal__fact">
                <Typography variant="text-4" color="subdued" translation="tickets">
                  Event
                </Typography>
                <Typography variant="text-3" translation={null}>
                  {formatEventDate(preview.eventDate, preview.eventTime, language)}
                  {preview.daysUntilEvent !== null && (
                    <strong>
                      {' · '}
                      {daysUntilEventLabel(preview.daysUntilEvent, t)}
                    </strong>
                  )}
                </Typography>
              </Box>
              <Box className="ticket-cancellation-modal__fact">
                <Typography variant="text-4" color="subdued" translation="tickets">
                  Booked on
                </Typography>
                <Typography variant="text-3" translation={null}>
                  {formatDateTime(preview.bookedAt, language)}
                </Typography>
              </Box>
              {preview.payments.map((payment) => (
                <Box key={payment.id} className="ticket-cancellation-modal__fact">
                  <Typography variant="text-4" color="subdued" translation="tickets">
                    {payment.status === 'paid' ? 'Paid on' : 'Payment'}
                  </Typography>
                  <Typography variant="text-3" translation={null}>
                    {payment.status === 'paid'
                      ? formatDateTime(payment.paidAt, language)
                      : t(payment.status)}
                    {' · '}
                    {paymentMethodLabel(payment, t)}
                    {' · '}
                    {euros(payment.amount)}
                  </Typography>
                </Box>
              ))}
              <Box className="ticket-cancellation-modal__fact">
                <Typography variant="text-4" color="subdued" translation="tickets">
                  Paid in total
                </Typography>
                <Typography variant="text-3" translation={null}>
                  {euros(preview.totals.paid)}
                  {preview.totals.serviceFee > 0 &&
                    ` (${t('incl. {{amount}} service fee', {
                      amount: euros(preview.totals.serviceFee),
                    })})`}
                </Typography>
              </Box>
              {alreadyRefunded > 0 && (
                <Box className="ticket-cancellation-modal__fact">
                  <Typography variant="text-4" color="subdued" translation="tickets">
                    Already refunded
                  </Typography>
                  <Typography variant="text-3" translation={null}>
                    {euros(alreadyRefunded)}
                  </Typography>
                </Box>
              )}
            </Box>

            {alreadyCancelled && (
              <Typography
                variant="text-3"
                translation="tickets"
                className="ticket-cancellation-modal__warning"
                block
              >
                This ticket has already been cancelled.
              </Typography>
            )}

            <Box className="ticket-cancellation-modal__section-header">
              <Typography variant="text-4" color="subdued" translation="tickets">
                Template
              </Typography>
              <Button
                variant="transparent"
                translation="tickets"
                href={`/${restaurantId}/settings/reservations/ticket-cancellation`}
                target="_blank"
                startIcon={(props) => <OpenInNew {...props} />}
                typographyProps={{ variant: 'text-4' }}
                className="ticket-cancellation-modal__manage-templates"
              >
                {templates.length ? 'Manage templates' : 'Create templates'}
              </Button>
            </Box>

            {!alreadyCancelled && <Box className="ticket-cancellation-modal__choices">
              {templates.map((option) => (
                <Box
                  key={option.id}
                  className={classHelper([
                    'ticket-cancellation-modal__choice',
                    choice === option.id && 'ticket-cancellation-modal__choice--selected',
                  ])}
                >
                  <Checkbox
                    radio
                    checked={choice === option.id}
                    onChange={() => selectTemplate(option)}
                    label={option.name}
                    translation={null}
                    labelColor="textPrimary"
                  />
                  <Typography
                    variant="text-4"
                    color="subdued"
                    translation={null}
                    className="ticket-cancellation-modal__choice-details"
                    block
                  >
                    {describeCancellationTemplate(option, t, euros)}
                    {' → '}
                    <strong>{templateAmountLabel(option)}</strong>
                    {suggestedTemplate?.id === option.id && (
                      <span className="ticket-cancellation-modal__suggested">
                        {t('Suggested for this date')}
                      </span>
                    )}
                  </Typography>
                </Box>
              ))}
              <Box
                className={classHelper([
                  'ticket-cancellation-modal__choice',
                  choice === WITHOUT_TEMPLATE &&
                    'ticket-cancellation-modal__choice--selected',
                ])}
              >
                <Checkbox
                  radio
                  checked={choice === WITHOUT_TEMPLATE}
                  onChange={continueWithoutTemplate}
                  label="Continue without a template"
                  translation="tickets"
                  labelColor="textPrimary"
                />
                <Typography
                  variant="text-4"
                  color="subdued"
                  translation="tickets"
                  className="ticket-cancellation-modal__choice-details"
                  block
                >
                  Set the refund yourself.
                </Typography>
              </Box>
            </Box>}

            {!alreadyCancelled && !choice && (
              <Typography
                variant="text-3"
                translation="tickets"
                className="ticket-cancellation-modal__notice"
                block
              >
                Choose a template or continue without one.
              </Typography>
            )}

            {!!template && !showOptions && (
              <Button
                variant="transparent"
                translation="tickets"
                typographyProps={{ variant: 'text-4' }}
                className="ticket-cancellation-modal__customize"
                onClick={() => setOptionsOpen(true)}
              >
                Adjust refund
              </Button>
            )}

            {templateNeedsMollie && (
              <Typography
                variant="text-4"
                translation="tickets"
                className="ticket-cancellation-modal__warning"
                block
              >
                This template pays back via Mollie, which is not possible for this ticket. Choose another refund method.
              </Typography>
            )}

            {!alreadyCancelled && !!choice && showOptions && (
              <Box className="ticket-cancellation-modal__options-panel">
                <Typography variant="text-4" color="subdued" translation="tickets">
                  Refund amount
                </Typography>
                <Box className="ticket-cancellation-modal__options">
                  {CALCULATION_OPTIONS.map((option) => (
                    <Checkbox
                      key={option.type}
                      radio
                      name="ticket-cancellation-calculation"
                      checked={calculationType === option.type}
                      onChange={() => {
                        setCalculationType(option.type);
                        setValueInput('');
                      }}
                      // "Full amount" means everything the guest paid, unless the user unticks the fee.
                      // onClick, because onChange skips a radio that is already checked.
                      onClick={() => {
                        if (option.type === 'full') setIncludeServiceFee(true);
                      }}
                      label={option.label}
                      translation="tickets"
                    />
                  ))}
                </Box>

                {needsValue && (
                  <TextField
                    fullWidth
                    label={calculationType === 'percentage' ? 'Percentage' : 'Amount'}
                    labelTranslation="tickets"
                    value={valueInput}
                    onChange={(event) => setValueInput(event.target.value)}
                    inputProps={{ inputMode: 'decimal' }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornmentAtom position="end">
                          <Typography variant="text-2" translation={null}>
                            {calculationType === 'percentage' ? '%' : '€'}
                          </Typography>
                        </InputAdornmentAtom>
                      ),
                    }}
                  />
                )}

                {preview.totals.serviceFee > 0 && calculationType !== 'fixed' && (
                  <Checkbox
                    checked={includeServiceFee}
                    onChange={(_, checked) => setIncludeServiceFee(checked)}
                    label={t('Include the service fee of {{amount}}', {
                      amount: euros(preview.totals.serviceFee),
                    })}
                    translation={null}
                  />
                )}

                <Typography variant="text-4" color="subdued" translation="tickets">
                  Refund method
                </Typography>
                <Box className="ticket-cancellation-modal__options">
                  <Checkbox
                    radio
                    checked={refundMethod === 'mollie'}
                    disabled={!mollieUsable}
                    onChange={() => setRefundMethod('mollie')}
                    label="Pay back via Mollie"
                    translation="tickets"
                  />
                  <Typography
                    variant="text-4"
                    color="subdued"
                    translation={null}
                    className="ticket-cancellation-modal__hint"
                    block
                  >
                    {mollieUsable
                      ? t('Up to {{amount}}, taken from your Mollie balance.', {
                          amount: euros(preview.totals.mollieRefundable),
                        })
                      : !preview.mollieAvailable
                      ? t('Mollie cannot be reached right now.')
                      : refundHint !== 'none'
                      ? refundHint === 'paypal'
                        ? t('PayPal payments can only be refunded through Mollie for 180 days. Issue a voucher or pay it back yourself by bank transfer.')
                        : t('Mollie can no longer refund this payment. Issue a voucher or pay it back yourself by bank transfer.')
                      : t('There is no Mollie payment left to refund.')}
                  </Typography>
                  <Checkbox
                    radio
                    checked={refundMethod === 'voucher'}
                    disabled={preview.totals.paid <= 0}
                    onChange={() => setRefundMethod('voucher')}
                    label="Issue a voucher"
                    translation="tickets"
                  />
                  <Checkbox
                    radio
                    checked={refundMethod === 'originalVoucher'}
                    disabled={!(preview.totals.originalVoucher && preview.totals.originalVoucher > 0)}
                    onChange={() => setRefundMethod('originalVoucher')}
                    label="Restore the original voucher"
                    translation="tickets"
                  />
                  <Checkbox
                    radio
                    checked={refundMethod === 'none'}
                    onChange={() => setRefundMethod('none')}
                    label="No refund"
                    translation="tickets"
                  />
                </Box>
              </Box>
            )}

            {allocationTargets.map(target => {
              const context = preview.accounting?.find(a => a.paymentId === target.paymentId);
              if (!context?.lines.length || context.lines.reduce((sum, l) => sum + l.remainingCents, 0) === target.amountCents ||
                (splitWithoutFee && allocationWithoutServiceFee(context.lines, target.amountCents))) return null;
              return <Box key={target.paymentId}>
                <Typography translation={null} block>{t('Assign {{amount}} to invoice items', { amount: euros(target.amountCents / 100) })}</Typography>
                {context.lines.filter(l => l.remainingCents > 0).map(line => <TextField key={line.index} fullWidth label={`${line.title} (${line.vatRate} %, ${euros(line.remainingCents / 100)})`} labelTranslation={null}
                  value={allocationInputs[`${target.paymentId}:${line.index}`] ?? ''} inputProps={{ inputMode: 'decimal' }}
                  onChange={event => setAllocationInputs(old => ({ ...old, [`${target.paymentId}:${line.index}`]: event.target.value }))} />)}
              </Box>;
            })}
            {!!allocationError && <Typography translation={null} block>{allocationError}</Typography>}
            {!alreadyCancelled && !!choice && (
              <>
                <Box className="ticket-cancellation-modal__total">
                  <Box className="ticket-cancellation-modal__total-row">
                    <Typography variant="text-3" translation={null}>
                      {effectiveMethod === 'mollie'
                        ? t('The guest gets back via Mollie')
                        : effectiveMethod === 'voucher'
                        ? t('The guest gets a voucher of')
                        : t('The guest gets back')}
                    </Typography>
                    <Typography variant="h5" translation={null}>
                      {euros(effectiveMethod === 'none' ? 0 : refundAmount)}
                    </Typography>
                  </Box>
                  {!!refundDelivery && (
                    <Typography
                      variant="text-4"
                      translation={null}
                      className={classHelper([
                        'ticket-cancellation-modal__delivery',
                        effectiveMethod === 'voucher' &&
                          !guestEmail &&
                          'ticket-cancellation-modal__delivery--manual',
                      ])}
                      block
                    >
                      {refundDelivery}
                    </Typography>
                  )}
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Reason"
                  labelTranslation="tickets"
                  helperText="The guest sees the reason in the cancellation email."
                  helperTextTranslation="tickets"
                  value={reason}
                  inputProps={{ maxLength: 500 }}
                  onChange={(event) => setReason(event.target.value)}
                />

                <Checkbox
                  checked={notifyGuest && !!guestEmail}
                  disabled={!guestEmail}
                  onChange={(_, checked) => setNotifyGuest(checked)}
                  label="Send the guest a cancellation email"
                  translation="tickets"
                />

                {preview.hasPendingPayments && (
                  <Typography
                    variant="text-4"
                    translation="tickets"
                    className="ticket-cancellation-modal__warning"
                    block
                  >
                    Open payments are stopped where possible. Later payments are automatically refunded and remain visible until confirmed.
                  </Typography>
                )}

                {signatureRequired && (
                  <SignatureDropdown
                    value={signature}
                    onChange={setSignature}
                    titleHasMargin
                  />
                )}

                {!!(error || validationError) && (
                  <Typography
                    variant="text-4"
                    translation={null}
                    className="ticket-cancellation-modal__error"
                    block
                  >
                    {error || validationError}
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default TicketCancellationModal;
